import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { IPaginatedResponse } from '@boma/client-shared';

// Enums matching backend
export enum ImportItemStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Rejected = 'Rejected',
  Imported = 'Imported',
  Delivered = 'Delivered'
}

export enum Timespan {
  None = 0,
  Year = 1,
  Month = 2,
  Week = 3,
  Day = 4,
  Hour = 5,
  Minute = 6,
  Second = 7
}

// Response interfaces
export interface IZraImport {
  id: string;
  supplierName: string;
  taskCode: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface IZraImportedItem {
  id: string;
  itemName: string;
  itemCode: string;
  taskCode: string;
  quantity: number;
  status: string;
  importDate: string;
}

export interface IProcessImportRequest {
  importItemStatus: ImportItemStatus;
  itemId?: number; // Required when accepting, null when rejecting
}

export interface IAcceptPurchaseItem {
  itemId: number;
  purchaseItemId: number;
}

export interface IAcceptPurchaseRequest {
  purchaseId: string;
  items: IAcceptPurchaseItem[];
}

export interface IAcceptPurchaseResponse {
  success: boolean;
  message: string;
}

export interface IProcessImportResponse {
  success: boolean;
  message: string;
  taskCode: string;
}

export interface IAcceptImportBody {
  productId: string;
  quantity: number;
  locationId?: string;
}

// Legacy interface - keep for backwards compatibility
export interface IImportItem {
  id: number;
  taskCode: string;
  declarationDate: string;
  declarationDateTime: string;
  itemSequence: number;
  declarationNumber: string;
  hsCode: string;
  name: string;
  importItemstatusCode: string;
  originNationCode: string;
  exportNationCode: string;
  package: number;
  packagingUnitCode: string;
  quantity: number;
  quantityUnitCode: string;
  grossWeight: number;
  netWeight: number;
  supplierName: string | null;
  agentName: string;
  invoiceForeignCurrencyAmount: number;
  invoiceForeignCurrency: string;
  invoiceForeignCurrencyExchangeRate: number;
  declarationReferenceNumber: string | null;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export interface IImportsResponse {
  errors: any[];
  message: string;
  succeeded: boolean;
  data: IImportItem[];
}

@Injectable({
  providedIn: 'root',
})
export class ImportsService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  /**
   * Log request details
   */
  private logRequest(method: string, endpoint: string, params?: any): void {
    console.log(`[Imports Service] ${method} Request:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      params: params || 'No params',
    });
  }

  /**
   * Log response details
   */
  private logResponse(method: string, endpoint: string, response: any): void {
    console.log(`[Imports Service] ${method} Response:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      response,
    });
  }

  /**
   * Log error details
   */
  private logError(method: string, endpoint: string, error: any): void {
    console.error(`[Imports Service] ${method} Error:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      error: error?.error || error?.message || error,
      status: error?.status,
      statusText: error?.statusText,
    });
  }

  /**
   * Get all ZRA imports with optional filtering
   * Endpoint: GET /api/v{version}/{entityId}/ZraImports/Imports
   */
  getZraImports(
    entityId: string,
    pageNumber: number = 1,
    pageSize: number = 25,
    startDate?: string,
    endDate?: string,
    supplier?: string,
    status?: string
  ): Observable<IPaginatedResponse<IZraImport>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    if (supplier) params = params.set('supplier', supplier);
    if (status) params = params.set('status', status);

    const endpoint = `${this.base}api/v${this.version}/${entityId}/imports/items`;
    this.logRequest('GET', endpoint, params);

    return this.httpClient.get<IPaginatedResponse<IZraImport>>(endpoint, { params }).pipe(
      tap((response) => this.logResponse('GET', endpoint, response)),
      catchError((error) => {
        this.logError('GET', endpoint, error);
        throw error;
      })
    );
  }

  /**
   * Get imported items summary with optional filtering
   * Endpoint: GET /api/v{version}/{entityId}/ZraImports/ImportedItems
   */
  getImportedItems(
    entityId: string,
    pageNumber: number = 1,
    pageSize: number = 50,
    startDate?: string,
    endDate?: string,
    itemStatus?: ImportItemStatus,
    timespan?: Timespan,
    query?: string
  ): Observable<IPaginatedResponse<IZraImportedItem>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    if (itemStatus) params = params.set('itemStatus', itemStatus);
    if (timespan !== undefined && timespan !== null) params = params.set('timespan', timespan.toString());
    if (query) params = params.set('query', query);

    const endpoint = `${this.base}api/v${this.version}/${entityId}/imports/items`;
    this.logRequest('GET', endpoint, params);

    return this.httpClient.get<IPaginatedResponse<IZraImportedItem>>(endpoint, { params }).pipe(
      tap((response) => this.logResponse('GET', endpoint, response)),
      catchError((error) => {
        this.logError('GET', endpoint, error);
        throw error;
      })
    );
  }

  /**
   * Process an import item (accept or reject) using path parameters
   * Accept: GET /api/v{version}/{entityId}/imports/process/{taskCode}/3/{itemId}
   * Reject: GET /api/v{version}/{entityId}/imports/process/{taskCode}/4
   * Status codes: 3 = Accepted, 4 = Rejected
   */
  processImportItem(
    entityId: string,
    taskCode: string,
    status: 'Accepted' | 'Rejected',
    itemId?: number,
    body?: IAcceptImportBody
  ): Observable<IProcessImportResponse> {
    const statusCode = status === 'Accepted' ? 3 : 4;

    const endpoint = status === 'Accepted' && itemId != null
      ? `${this.base}api/v${this.version}/${entityId}/imports/process/${taskCode}/${statusCode}/${itemId}`
      : `${this.base}api/v${this.version}/${entityId}/imports/process/${taskCode}/${statusCode}`;

    console.log(`[Imports Service] Processing import item:`, {
      taskCode,
      status,
      statusCode,
      itemId,
      endpoint,
      body
    });

    if (status === 'Accepted') {
      this.logRequest('POST', endpoint, body);
      return this.httpClient.post<IProcessImportResponse>(endpoint, body ?? {}).pipe(
        tap((response) => {
          console.log(`✅ [Imports Service] Import item accepted successfully:`, response);
          this.logResponse('POST', endpoint, response);
        }),
        catchError((error) => {
          console.error(`❌ [Imports Service] Failed to accept import item:`, error);
          this.logError('POST', endpoint, error);
          throw error;
        })
      );
    }

    this.logRequest('GET', endpoint);
    return this.httpClient.get<IProcessImportResponse>(endpoint).pipe(
      tap((response) => {
        console.log(`✅ [Imports Service] Import item rejected successfully:`, response);
        this.logResponse('GET', endpoint, response);
      }),
      catchError((error) => {
        console.error(`❌ [Imports Service] Failed to reject import item:`, error);
        this.logError('GET', endpoint, error);
        throw error;
      })
    );
  }

  /**
   * Legacy method - Get all import items
   * Endpoint: GET /api/v{version}/{entityId}/imports/legacy?importerTpin={tpin}
   */
  getImports(entityId: string, importerTpin?: string, page: number = 1, pageSize: number = 20): Observable<IImportsResponse> {
    let params = new HttpParams()
      .set('pageNumber', page.toString())
      .set('pageSize', pageSize.toString());
    if (importerTpin) params = params.set('importerTpin', importerTpin);

    const endpoint = `${this.base}api/v${this.version}/${entityId}/imports/items`;
    this.logRequest('GET', endpoint, params);

    return this.httpClient.get<IImportsResponse>(endpoint, { params }).pipe(
      tap((response) => this.logResponse('GET', endpoint, response)),
      catchError((error) => {
        this.logError('GET', endpoint, error);
        throw error;
      })
    );
  }

  /**
   * Legacy: Approve/Accept an import item
   * @deprecated Use processImportItem() instead
   */
  approveImport(entityId: string, importId: number): Observable<IImportItem> {
    console.warn('[Imports Service] approveImport() is deprecated. Use processImportItem() instead.');
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ImportItem/${importId}/approve`;
    this.logRequest('POST', endpoint);

    return this.httpClient.post<IImportItem>(endpoint, {}).pipe(
      tap((response) => this.logResponse('POST', endpoint, response)),
      catchError((error) => {
        this.logError('POST', endpoint, error);
        throw error;
      })
    );
  }

  /**
   * Accept a purchase with item mappings
   * Endpoint: POST /api/v{version}/{entityId}/purchases/accept
   */
  acceptPurchase(
    entityId: string,
    purchaseId: string,
    items: IAcceptPurchaseItem[]
  ): Observable<IAcceptPurchaseResponse> {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/purchases/accept`;
    const payload: IAcceptPurchaseRequest = { purchaseId, items };

    this.logRequest('POST', endpoint, payload);

    return this.httpClient.post<IAcceptPurchaseResponse>(endpoint, payload).pipe(
      tap((response) => {
        console.log(`✅ [Imports Service] Purchase accepted successfully:`, response);
        this.logResponse('POST', endpoint, response);
      }),
      catchError((error) => {
        console.error(`❌ [Imports Service] Failed to accept purchase:`, error);
        this.logError('POST', endpoint, error);
        throw error;
      })
    );
  }

  /**
   * Legacy: Reject an import item
   * @deprecated Use processImportItem() instead
   */
  rejectImport(
    entityId: string,
    importId: number,
    rejectionReason?: string
  ): Observable<IImportItem> {
    console.warn('[Imports Service] rejectImport() is deprecated. Use processImportItem() instead.');
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ImportItem/${importId}/reject`;
    const payload = { reason: rejectionReason };
    this.logRequest('POST', endpoint, payload);

    return this.httpClient.post<IImportItem>(endpoint, payload).pipe(
      tap((response) => this.logResponse('POST', endpoint, response)),
      catchError((error) => {
        this.logError('POST', endpoint, error);
        throw error;
      })
    );
  }
}
